import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { 
  ArrowRight, 
  Search,
  ClipboardCheck,
  Settings,
  Compass,
  TrendingUp
} from "lucide-react";

const COMPASS_IMG = "https://images.unsplash.com/photo-1488375634201-b85b28653a79?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NzF8MHwxfHNlYXJjaHwzfHxhbnRpcXVlJTIwbmF1dGljYWwlMjBjb21wYXNzJTIwY2hhcnQlMjBjbG9zZSUyMHVwJTIwZGFya3xlbnwwfHx8fDE3NzMxNjU2NDd8MA&ixlib=rb-4.1.0&q=85";

const fadeUp = {
  hidden: { opacity: 0, y: 40 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.8, ease: [0.22, 1, 0.36, 1] } }
};

const staggerContainer = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.15 } }
};

const steps = [
  {
    number: "01",
    icon: Search,
    title: "Identify Promising Businesses",
    description: "We begin by identifying businesses that demonstrate strong fundamentals but require strategic support to reach their full potential. We look for companies with solid foundations, committed leadership, and clear growth opportunities.",
    details: [
      "Assessment of market position and competitive landscape",
      "Evaluation of leadership team and organizational culture",
      "Analysis of growth potential and scalability"
    ]
  },
  {
    number: "02",
    icon: ClipboardCheck,
    title: "Evaluate Operational & Leadership Needs",
    description: "Through comprehensive evaluation, we identify gaps in operations, leadership, and strategy that are limiting growth. This phase creates a clear picture of what needs to change and why.",
    details: [
      "Deep-dive operational assessment",
      "Leadership capability analysis",
      "Financial health and structure review"
    ]
  },
  {
    number: "03",
    icon: Settings,
    title: "Implement Structured Management Oversight",
    description: "We work alongside leadership to implement proven management frameworks, accountability structures, and operational improvements. This hands-on phase focuses on building sustainable systems.",
    details: [
      "Management framework implementation",
      "Performance metrics and KPI systems",
      "Process optimization and documentation"
    ]
  },
  {
    number: "04",
    icon: Compass,
    title: "Provide Strategic Direction & Capital",
    description: "Where appropriate, we provide both strategic guidance and investment capital to accelerate transformation. Our involvement goes beyond advice—we commit resources to shared success.",
    details: [
      "Strategic planning and execution support",
      "Capital deployment for growth initiatives",
      "Board-level guidance and governance"
    ]
  },
  {
    number: "05",
    icon: TrendingUp,
    title: "Guide Long-Term Growth & Sustainability",
    description: "Our engagement doesn't end with initial improvements. We maintain active involvement to ensure sustainable growth, helping businesses navigate challenges and capitalize on opportunities.",
    details: [
      "Ongoing strategic advisory",
      "Performance monitoring and optimization",
      "Long-term value creation planning"
    ]
  }
];

export default function ApproachPage() {
  return (
    <div data-testid="approach-page">
      {/* Hero Section */}
      <section className="pt-32 pb-24 md:pt-40 md:pb-32 px-6 md:px-12 lg:px-24 bg-navy-950">
        <motion.div
          initial="hidden"
          animate="visible"
          variants={staggerContainer}
          className="max-w-4xl"
        >
          <motion.p 
            className="text-teal-500 text-xs uppercase tracking-[0.3em] font-semibold mb-4"
            variants={fadeUp}
          >
            Our Approach
          </motion.p>
          <motion.h1 
            className="font-playfair text-4xl sm:text-5xl lg:text-6xl font-bold text-white leading-tight mb-8"
            variants={fadeUp}
          >
            The Venture Winds Methodology
          </motion.h1>
          <motion.p 
            className="text-slate-400 text-lg md:text-xl leading-relaxed max-w-2xl"
            variants={fadeUp}
          >
            A systematic, hands-on approach to business transformation that goes 
            beyond traditional consulting to create lasting, measurable impact.
          </motion.p>
        </motion.div>
      </section>

      {/* Methodology Steps */}
      <section className="py-24 md:py-32 px-6 md:px-12 lg:px-24" data-testid="methodology-section">
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={staggerContainer}
          className="max-w-5xl mx-auto"
        >
          <motion.div className="text-center mb-20" variants={fadeUp}>
            <h2 className="font-playfair text-3xl md:text-4xl font-semibold text-navy-950 mb-6">
              Five Phases of Transformation
            </h2>
            <p className="text-slate-500 text-lg max-w-2xl mx-auto">
              Our methodology guides businesses through a proven journey from 
              assessment to sustainable growth.
            </p>
          </motion.div>

          <div className="space-y-0">
            {steps.map((step, index) => (
              <motion.div
                key={step.number}
                variants={fadeUp}
                className={`grid lg:grid-cols-2 gap-8 lg:gap-16 py-16 ${
                  index !== steps.length - 1 ? "border-b border-slate-200" : ""
                }`}
                data-testid={`approach-step-${index}`}
              >
                <div className={index % 2 === 1 ? "lg:order-2" : ""}>
                  <div className="flex items-center gap-4 mb-6">
                    <span className="text-teal-500 font-playfair text-5xl font-bold">
                      {step.number}
                    </span>
                    <step.icon className="w-8 h-8 text-navy-950" strokeWidth={1.5} />
                  </div>
                  <h3 className="font-playfair text-2xl md:text-3xl font-semibold text-navy-950 mb-4">
                    {step.title}
                  </h3>
                  <p className="text-slate-500 text-lg leading-relaxed">
                    {step.description}
                  </p>
                </div>

                <div className={`bg-slate-50 p-8 ${index % 2 === 1 ? "lg:order-1" : ""}`}>
                  <h4 className="text-sm uppercase tracking-widest text-navy-950 font-semibold mb-6">
                    Key Activities
                  </h4>
                  <ul className="space-y-4">
                    {step.details.map((detail, i) => (
                      <li key={i} className="flex items-start gap-3">
                        <div className="w-1.5 h-1.5 bg-teal-500 rounded-full mt-2 flex-shrink-0" />
                        <span className="text-slate-600">{detail}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </motion.div>
            ))}
          </div>
        </motion.div>
      </section>

      {/* Differentiator Section */}
      <section className="py-24 md:py-32 px-6 md:px-12 lg:px-24 bg-slate-50">
        <div className="grid lg:grid-cols-2 gap-16 items-center">
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <img
              src={COMPASS_IMG}
              alt="Antique nautical compass"
              className="w-full aspect-square object-cover"
            />
          </motion.div>

          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={staggerContainer}
          >
            <motion.p 
              className="text-teal-600 text-xs uppercase tracking-[0.3em] font-semibold mb-4"
              variants={fadeUp}
            >
              What Sets Us Apart
            </motion.p>
            <motion.h2 
              className="font-playfair text-3xl md:text-4xl font-semibold text-navy-950 mb-6"
              variants={fadeUp}
            >
              Beyond Traditional Consulting
            </motion.h2>
            <motion.div className="space-y-6" variants={staggerContainer}>
              {[
                {
                  title: "Hands-On Partnership",
                  desc: "We don't deliver reports and leave. We work alongside your team to implement real change."
                },
                {
                  title: "Aligned Incentives",
                  desc: "Our success is tied to yours. We often invest capital alongside our consulting engagement."
                },
                {
                  title: "Long-Term Commitment",
                  desc: "We maintain active involvement to ensure sustainable results, not just short-term improvements."
                },
                {
                  title: "Selective Approach",
                  desc: "We work with a limited number of businesses, allowing deep focus on each partnership."
                }
              ].map((item, i) => (
                <motion.div key={i} variants={fadeUp} className="border-l-2 border-teal-500 pl-6">
                  <h3 className="font-semibold text-navy-950 mb-1">{item.title}</h3>
                  <p className="text-slate-500">{item.desc}</p>
                </motion.div>
              ))}
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 md:py-32 px-6 md:px-12 lg:px-24 bg-navy-950">
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={staggerContainer}
          className="text-center max-w-3xl mx-auto"
        >
          <motion.h2 
            className="font-playfair text-3xl md:text-4xl font-semibold text-white mb-6"
            variants={fadeUp}
          >
            Ready to Begin Your Transformation?
          </motion.h2>
          <motion.p 
            className="text-slate-400 text-lg leading-relaxed mb-10"
            variants={fadeUp}
          >
            Let's discuss how our methodology can address your specific 
            business challenges and growth objectives.
          </motion.p>
          <motion.div className="flex flex-col sm:flex-row gap-4 justify-center" variants={fadeUp}>
            <Link
              to="/contact"
              className="inline-flex items-center justify-center gap-2 bg-teal-500 text-white px-8 py-4 text-sm uppercase tracking-widest font-medium hover:bg-teal-600 transition-colors"
              data-testid="approach-cta"
            >
              Start the Conversation
              <ArrowRight className="w-4 h-4" />
            </Link>
            <Link
              to="/partnerships"
              className="inline-flex items-center justify-center gap-2 bg-transparent border border-white/30 text-white px-8 py-4 text-sm uppercase tracking-widest font-medium hover:bg-white/10 transition-colors"
              data-testid="partnerships-link"
            >
              Partnership Criteria
            </Link>
          </motion.div>
        </motion.div>
      </section>
    </div>
  );
}
